import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  api,
  termLabel,
  type ExamMonitor,
  type FlaggedAttempt,
  type MarkingRow,
  type MonitoredExam,
} from "../api.js";

/** How long ago, in the coarsest unit that is still true. */
function ago(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

/** Time left on a window, or null once it has passed. */
function remaining(iso: string): string | null {
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return null;
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `${mins}m left`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ${mins % 60}m left`;
  return `${Math.floor(hours / 24)}d left`;
}

/**
 * Exams and marking across the institution.
 *
 * The live section is the point: while a lab exam is being sat, an admin wants
 * how many are in the room, how many have finished, and whether the proctoring
 * rules are being tripped -- without opening each exam or interrupting the
 * teacher running it. It refreshes on its own, because a screen you have to
 * remember to reload is not a monitor.
 */
export default function Monitor() {
  const [exams, setExams] = useState<ExamMonitor | null>(null);
  const [flagged, setFlagged] = useState<FlaggedAttempt[]>([]);
  const [marking, setMarking] = useState<MarkingRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [refreshedAt, setRefreshedAt] = useState<Date | null>(null);

  const load = () =>
    Promise.all([
      api.get<ExamMonitor>("/api/institution/exams"),
      api.get<{ attempts: FlaggedAttempt[] }>("/api/institution/violations"),
      api.get<{ assignments: MarkingRow[] }>("/api/institution/marking"),
    ])
      .then(([e, v, m]) => {
        setExams(e);
        setFlagged(v.attempts);
        setMarking(m.assignments);
        setRefreshedAt(new Date());
        setError(null);
      })
      .catch((err) => setError(err.message));

  useEffect(() => {
    void load();
  }, []);

  // Poll only while something is actually live. A monitor that keeps hitting
  // the server all week to redraw an empty page is just noise.
  const liveCount = exams?.live.length ?? 0;
  useEffect(() => {
    if (liveCount === 0) return;
    const timer = setInterval(() => void load(), 30_000);
    return () => clearInterval(timer);
  }, [liveCount]);

  return (
    <div className="page">
      <div className="between" style={{ marginBottom: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22 }}>Exams &amp; marking</h1>
          <div className="muted">
            {liveCount > 0
              ? `${liveCount} exam${liveCount === 1 ? "" : "s"} open now · refreshing every 30s`
              : "Nothing open right now"}
            {refreshedAt && ` · updated ${refreshedAt.toLocaleTimeString()}`}
          </div>
        </div>
        <div className="row">
          <button onClick={() => void load()}>Refresh</button>
          <Link to="/"><button>Institution</button></Link>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {exams && (
        <>
          <Section title="OPEN NOW" exams={exams.live} empty="No exam is open." live />
          <Section title="UPCOMING" exams={exams.upcoming} empty="Nothing scheduled." />
          {exams.drafts.length > 0 && (
            <>
              <Section
                title="UNPUBLISHED"
                exams={exams.drafts}
                empty=""
                note="Students cannot see or sit these. An exam still unpublished as its window opens is one nobody can take."
              />
            </>
          )}
          <Section title="RECENTLY CLOSED" exams={exams.recent} empty="Nothing in the last week." />
        </>
      )}

      <div className="card">
        <div className="between" style={{ marginBottom: 10 }}>
          <strong>Proctoring flags</strong>
          <span className="muted" style={{ fontSize: 13 }}>
            Attempts that tripped an exam's rules
          </span>
        </div>
        {flagged.length === 0 ? (
          <div className="muted">No violations recorded.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Student</th><th>Exam</th><th>Violations</th><th>Policy</th>
                <th>Status</th><th>Started</th><th>IP</th>
              </tr>
            </thead>
            <tbody>
              {flagged.map((a) => (
                <tr key={a.id}>
                  <td>
                    {a.student.name}
                    <div className="muted" style={{ fontSize: 12 }}>
                      {a.student.rollNumber ?? a.student.email}
                    </div>
                  </td>
                  <td>
                    {a.exam.title}
                    <div className="muted" style={{ fontSize: 12 }}>{a.exam.course.code}</div>
                  </td>
                  <td>
                    <span className={`badge ${a.overThreshold ? "fail" : "warn"}`}>
                      {a.violationCount} / {a.exam.maxViolations}
                    </span>
                  </td>
                  <td className="muted">
                    {a.exam.onViolationExceeded.toLowerCase().replace(/_/g, " ")}
                  </td>
                  <td className="muted">{a.status.toLowerCase().replace(/_/g, " ")}</td>
                  <td className="muted">{ago(a.startedAt)}</td>
                  <td className="muted">{a.ipAtStart || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="card">
        <div className="between" style={{ marginBottom: 10 }}>
          <strong>Awaiting teacher review</strong>
          <span className="muted" style={{ fontSize: 13 }}>Oldest first</span>
        </div>
        <p className="muted" style={{ fontSize: 13, marginTop: 0 }}>
          Every submission is auto-scored already. These are the ones no teacher has looked at,
          which is what a contested mark or a partial-credit case needs.
        </p>
        {marking.length === 0 ? (
          <div className="muted">Nothing waiting.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Assignment</th><th>Course</th><th>Teacher</th><th>Due</th>
                <th>Waiting</th><th>Oldest</th><th />
              </tr>
            </thead>
            <tbody>
              {marking.map((m) => (
                <tr key={m.id}>
                  <td>{m.title}</td>
                  <td className="muted">
                    {m.course.code}
                    <div style={{ fontSize: 12 }}>{termLabel(m.course)}</div>
                  </td>
                  <td className="muted">
                    {m.teachers.map((t) => t.name).join(", ") || <span className="badge warn">none</span>}
                  </td>
                  <td className="muted">
                    {m.dueAt ? new Date(m.dueAt).toLocaleDateString() : "—"}
                    {m.pastDue && <span className="badge draft" style={{ marginLeft: 6 }}>past</span>}
                  </td>
                  <td>
                    <span className="badge warn">
                      {m.pendingCount} of {m.submissionCount}
                    </span>
                  </td>
                  <td className="muted">{m.oldestPendingAt ? ago(m.oldestPendingAt) : "—"}</td>
                  <td>
                    <Link to={`/assignments/${m.id}/submissions`}><button>Open</button></Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Section({
  title,
  exams,
  empty,
  live,
  note,
}: {
  title: string;
  exams: MonitoredExam[];
  empty: string;
  live?: boolean;
  note?: string;
}) {
  if (exams.length === 0 && !empty) return null;

  return (
    <div className="card">
      <div style={{ marginBottom: 10, color: "var(--muted)", fontSize: 12 }}>{title}</div>
      {note && <p className="muted" style={{ fontSize: 13, marginTop: 0 }}>{note}</p>}
      {exams.length === 0 ? (
        <div className="muted">{empty}</div>
      ) : (
        exams.map((e) => {
          const left = remaining(e.closesAt);
          return (
            <div
              key={e.id}
              className="between"
              style={{
                padding: "10px 0",
                borderTop: "1px solid var(--border)",
                flexWrap: "wrap",
                gap: 10,
              }}
            >
              <div style={{ minWidth: 220 }}>
                <div style={{ fontWeight: 600 }}>
                  {e.title}{" "}
                  {e.flaggedAttempts > 0 && (
                    <span className="badge fail">{e.flaggedAttempts} flagged</span>
                  )}
                </div>
                <div className="muted" style={{ fontSize: 13 }}>
                  {e.course.code} · {termLabel(e.course)} · {e.questionCount} question
                  {e.questionCount === 1 ? "" : "s"}
                  {e.durationMinutes ? ` · ${e.durationMinutes} min` : ""}
                </div>
                <div className="muted" style={{ fontSize: 12 }}>
                  {new Date(e.opensAt).toLocaleString()} — {new Date(e.closesAt).toLocaleString()}
                  {live && left && ` · ${left}`}
                </div>
              </div>

              <div className="row" style={{ flexWrap: "wrap" }}>
                <Tally value={e.inProgress} label="sitting" warn={live && e.inProgress > 0} />
                <Tally value={e.submitted} label="submitted" />
                {e.autoSubmitted > 0 && <Tally value={e.autoSubmitted} label="auto-submitted" />}
                {e.expired > 0 && <Tally value={e.expired} label="expired" />}
                <Tally value={e.notStarted} label="not started" />
                <span className="muted" style={{ fontSize: 12 }}>of {e.eligible} enrolled</span>
                <Link to={`/exams/${e.id}/edit`}><button>Open</button></Link>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}

function Tally({ value, label, warn }: { value: number; label: string; warn?: boolean }) {
  return (
    <div style={{ textAlign: "center", minWidth: 68 }}>
      <div style={{ fontSize: 18, fontWeight: 600, color: warn ? "var(--warn)" : undefined }}>
        {value}
      </div>
      <div className="muted" style={{ fontSize: 11 }}>{label}</div>
    </div>
  );
}
